// Stable type definitions for the AI-generated career report. Both the
// report-generator function (server) and the dashboard pages (client) import
// these. Schema is locked to the new single-page dashboard template:
// Profile → Personality (CAPBOI) → Careers → Strategy → Coaching.

export interface ReportRadarValues {
  communal: number;
  analytical: number;
  pragmatic: number;
  bold: number;
  orthodox: number;
  imaginative: number;
}

/** Single CAPBOI trait, scored 0–100 with rich descriptive copy. */
export interface ReportCapboiTrait {
  /** One-letter code: I/C/B/A/P/O. */
  key: 'I' | 'C' | 'B' | 'A' | 'P' | 'O';
  /** Display name: Imaginative / Communal / Bold / Analytical / Pragmatic / Orthodox. */
  name: string;
  /** Score 0–100. */
  score: number;
  /** One-liner (10–18 words) summarizing what high-scorers do. */
  one: string;
  /** Long description (2–3 sentences) tying the score to the user's answers. */
  long: string;
  /** "When this trait runs hot" descriptors — 2–3 short phrases (≤4 words). */
  high: string[];
  /** Trade-offs / blind spots — 1–2 short phrases. */
  low: string[];
}

export interface ReportPersonality {
  /** 6-letter code from dominant axes (e.g. "ICBAPO"). */
  code: string;
  /** Single-word friendly name (Catalyst, Mechanic, Strategist…). */
  typeName: string;
  /** One-sentence definition. */
  typeTagline: string;
  /** 2–4 sentence narrative tied to the user's quiz answers. */
  description: string;
  /** Two strongest axes — used in narrative copy. */
  dominantAxes: [string, string];
  /** 3–6 sentences elaborating on the dominant pair with growth notes. */
  dominantAxesDescription: string;
  /** Radar scores 0–100 — kept for backwards compatibility / radar viz. */
  radar: ReportRadarValues;
  /** Peer-cohort baseline (smooth, ~40–55 each). */
  peerRadar: ReportRadarValues;
  /** Six trait cards in fixed order (I, C, B, A, P, O). */
  capboi: ReportCapboiTrait[];
}

export interface ReportTraitCard {
  name: string;
  description: string;
}

/** Salary growth outlook. Sourced from BLS lookup; not generated. */
export interface ReportCareerGrowth {
  /** "+13%" formatted. */
  rate: string;
  /** "Faster than avg." / "Much faster" / "Steady growth" / "Avg. growth" / "Declining". */
  label: string;
  /** Visual indicator. */
  trend: 'up' | 'down' | 'flat';
}

export interface ReportCareer {
  /** Stable kebab-case slug. */
  id: string;
  title: string;
  /** Match score 0–100. */
  matchPercent: number;
  /** Industry / category — e.g. "Design & Product", "Tech & Product". */
  field: string;
  /** Salary range (USD). */
  salaryEntry: number;
  salaryMid: number;
  salarySenior: number;
  /** BLS 10-yr growth — joined client-side from the careerGrowth table. */
  growth: ReportCareerGrowth;
  /** 2–3 sentence career summary tailored to the user. */
  description: string;
  /** Why this fit was strong (3 short bullets). */
  fitReasons: string[];
  /** 3–4 skills the user already shows in their answers. */
  haveSkills: string[];
  /** 2–4 skills they'd need to develop. */
  needSkills: string[];
  /** 1-sentence description of a typical day. */
  daily: string;
}

export interface ReportBook {
  title: string;
  subtitle: string;
  author: string;
  /** Why this book was picked for this user — 1 short sentence. */
  why: string;
  /** Optional Amazon affiliate link. */
  amazonUrl?: string;
}

/** A single tickable task within a strategy phase. */
export interface ReportStrategyTask {
  /** Imperative action — under 100 chars. */
  t: string;
  /** Default checked state. Persisted client-side per session. */
  done: boolean;
}

export interface ReportStrategyPhase {
  /** Time bucket label — "Next 30 days" / "Days 31–60" / "Days 61–90" / "Year one". */
  label: string;
  /** Short title — under 50 chars (e.g. "Get oriented"). */
  title: string;
  /** 1-sentence goal for this phase. */
  goal: string;
  /** 3–4 tasks per phase. */
  tasks: ReportStrategyTask[];
}

export interface ReportStrategy {
  /** 1-sentence intro shown under the section heading. */
  intro: string;
  /** EXACTLY 4 phases, in order. */
  phases: ReportStrategyPhase[];
}

export interface Report {
  bio: string;
  educationLine: string;       // e.g. "Bachelor's Degree in Music · Colgate"
  currentTitleLine: string;    // e.g. "Startup CEO"
  personality: ReportPersonality;
  // Profile section content (separate from Personality)
  strengths: ReportTraitCard[];     // 3 entries
  strengthsSummary: string;
  weaknesses: ReportTraitCard[];    // 3 entries — framed as growth areas
  weaknessesSummary: string;
  workplaceTraits: ReportTraitCard[]; // 3 entries
  workplaceTraitsSummary: string;
  /** 8–10 entries, ordered descending by matchPercent. */
  careers: ReportCareer[];
  /** EXACTLY 6 entries — folded into Strategy "Reading list" as a 2x3 grid. */
  books: ReportBook[];
  /** 3 short statements (<100 chars each). */
  takeaways: string[];
  /** 4-phase 30/60/90 + year-one plan. */
  strategy: ReportStrategy;
}

export const REPORT_AXES: (keyof ReportRadarValues)[] = [
  'communal', 'analytical', 'pragmatic', 'bold', 'orthodox', 'imaginative',
];

/** Fixed display order for CAPBOI trait cards. */
export const CAPBOI_KEYS = ['I', 'C', 'B', 'A', 'P', 'O'] as const;
